import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AdminReadiness } from "../components/AdminReadiness";
import { RichTextEditor } from "../components/RichTextEditor";
import { DriveBrowser } from "../components/DriveBrowser";
import { toast } from "sonner";
import { Check, X, Download, Calendar as CalIcon, User as UserIcon, FileText, Video, Users as UsersIcon, Plug, KeyRound, Copy, Trash2, Sparkles, Mail, MailX, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import McpPanel from "../components/McpPanel";

export function AdminConsole({ embedded = false }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessClient, setAccessClient] = useState(null);
  const [accessIds, setAccessIds] = useState([]);
  const [engClient, setEngClient] = useState(null);
  const [engUrl, setEngUrl] = useState("");
  const [deals, setDeals] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [dealTask, setDealTask] = useState(null);
  const [dtForm, setDtForm] = useState({ title: "", due_date: "", assignee_id: "" });
  const [pwUser, setPwUser] = useState(null);
  const [pwText, setPwText] = useState("");
  const [addPartner, setAddPartner] = useState(false);
  const [apForm, setApForm] = useState({ name: "", email: "", sender_email: "", password: "", referral_code: "" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", company: "", phone: "", referral_code: "", business_email: "", assigned_ids: [] });
  const [editBusy, setEditBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [u, up, b, d, dir] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/uploads"),
        api.get("/admin/bookings"),
        api.get("/workspace/transactions").catch(() => ({ data: [] })),
        api.get("/workspace/directory").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data); setUploads(up.data); setBookings(b.data);
      setDeals(d.data || []); setDirectory(dir.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && user !== false && user.role === "admin") loadAll();
    else if (user !== null) setLoading(false);
  }, [user, loadAll]);

  if (user === null) return <CenterMsg>Loading…</CenterMsg>;
  if (user === false) return <Navigate to="/auth" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const approve = async (id) => {
    try { await api.post(`/admin/users/${id}/approve`); toast.success("User approved"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/users/${id}/reject`); toast.success("User rejected"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const savePw = async () => {
    if (pwText.trim().length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.put(`/admin/users/${pwUser.id}/password`, { password: pwText });
      toast.success(`Password updated for ${pwUser.name}`);
      setPwUser(null); setPwText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveAddPartner = async () => {
    if (!apForm.name.trim() || !apForm.email.trim() || apForm.password.trim().length < 8) {
      toast.error("Name, login email and an 8+ char password are required"); return;
    }
    try {
      await api.post("/admin/partners", {
        name: apForm.name.trim(), email: apForm.email.trim(),
        password: apForm.password, sender_email: apForm.sender_email.trim() || null,
        referral_code: apForm.referral_code.trim() || null,
      });
      toast.success("Partner created");
      setAddPartner(false);
      setApForm({ name: "", email: "", sender_email: "", password: "", referral_code: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openDealTask = (deal) => {
    const team = (deal.assigned_ids || []);
    setDtForm({ title: "", due_date: new Date().toISOString().slice(0, 10), assignee_id: team[0] || "" });
    setDealTask(deal);
  };
  const saveDealTask = async () => {
    if (!dtForm.title.trim()) { toast.error("Enter a task title"); return; }
    try {
      await api.post(`/workspace/transactions/${dealTask.id}/tasks`, {
        title: dtForm.title.trim(),
        due_date: dtForm.due_date,
        assignee_id: dtForm.assignee_id || (dealTask.assigned_ids || [])[0],
      });
      toast.success("Task added to deal");
      setDealTask(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const setWorkspaceRole = async (id, workspace_role) => {
    try {
      await api.post(`/admin/users/${id}/workspace-role`, { workspace_role });
      toast.success(workspace_role === "staff" ? "Promoted to workspace staff" : "Set to workspace client");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const pending = users.filter((u) => u.status === "pending");  const everyone = users.filter((u) => u.role !== "admin");
  const clients = everyone.filter((u) => u.account_type !== "partner" && u.account_type !== "underwriter");
  const partners = everyone.filter((u) => u.account_type === "partner");
  const staffMembers = everyone.filter((u) => (u.account_type === "partner" || u.account_type === "underwriter") && u.status === "approved");

  const openAccess = (u) => { setAccessClient(u); setAccessIds(u.assigned_ids || []); };
  const toggleAccessId = (id) => setAccessIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const saveAccess = async () => {
    try {
      await api.put(`/admin/users/${accessClient.id}/access`, { assigned_ids: accessIds });
      toast.success("Client access updated");
      setAccessClient(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleWorkspaceAccess = (u) => setWorkspaceRole(u.id, u.workspace_role === "staff" ? "client" : "staff");
  const toggleEmailSend = async (u) => {
    const next = u.email_send_enabled === false;  // currently disabled -> enable
    try {
      await api.put(`/admin/users/${u.id}/email-enabled`, { enabled: next });
      toast.success(next ? "Email sending enabled" : "Email sending disabled");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const toggleAiAccess = async (u) => {
    const next = !u.ai_assistant_enabled;
    try {
      await api.put(`/admin/users/${u.id}/ai-access`, { enabled: next });
      toast.success(next ? "AI assistant access granted" : "AI assistant access revoked");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveAiLimit = async (u, raw) => {
    const t = String(raw).trim();
    const n = t === "" ? null : parseInt(t, 10);
    if (n !== null && (isNaN(n) || n < 0 || n > 10000)) { toast.error("Enter a number between 0 and 10000"); return; }
    if ((u.ai_daily_limit ?? "") === (n ?? "")) return;
    try {
      await api.put(`/admin/users/${u.id}/ai-limit`, { limit: n });
      toast.success(n === null ? "Daily limit cleared — using default" : `Daily AI limit set to ${n}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "", email: u.email || "", company: u.company || "",
      phone: u.phone || "", referral_code: u.referral_code || "",
      business_email: u.business_email || "", assigned_ids: u.assigned_ids || [],
    });
  };
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const body = {
        name: editForm.name.trim(), email: editForm.email.trim(),
        company: editForm.company.trim(), phone: editForm.phone.trim(),
        referral_code: editForm.referral_code.trim(),
        business_email: editForm.business_email.trim() || undefined,
      };
      if (editUser.account_type !== "partner" && editUser.account_type !== "underwriter") {
        body.assigned_ids = editForm.assigned_ids;
      }
      await api.put(`/admin/users/${editUser.id}/profile`, body);
      toast.success("Profile updated");
      setEditUser(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setEditBusy(false); }
  };
  const removeAccess = async (id) => {
    try { await api.post(`/admin/users/${id}/reject`); toast.success("Access removed"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const openEngagement = (u) => { setEngClient(u); setEngUrl(u.engagement_letter_url || ""); };
  const saveEngagement = async () => {
    try {
      await api.put(`/admin/users/${engClient.id}/engagement-letter`, { url: engUrl });
      toast.success(engUrl.trim() ? "Engagement letter sent to client" : "Engagement letter link cleared");
      setEngClient(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const usersList = (list, opts = {}) => {
    const { onAccess, accessToggle, onEngagement } = opts;
    return (
    list.length === 0 ? <Empty text="No users in this group yet." /> : (
      <div className="border-y border-white/10 divide-y divide-white/10">
        {list.map((u) => (
          <div key={u.id} className="py-5 grid md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4 flex items-center gap-3">
              <UserIcon className="h-4 w-4 text-white/40" strokeWidth={1.25} />
              <div>
                <div className="font-display text-base">{u.name}</div>
                <div className="text-white/50 text-xs">{u.email}</div>
                <div className="label-kicker text-[10px] mt-1 text-white/40">
                  {u.account_type === "partner"
                    ? (u.workspace_role === "staff" ? "Partner · Workspace" : "Referral Partner")
                    : u.account_type === "underwriter" ? "Underwriter" : "Client"}
                </div>
              </div>
            </div>
            <div className="md:col-span-3 text-sm text-white/60">{u.company || ", "}</div>
            <div className="md:col-span-3 text-xs text-white/40">
              {new Date(u.created_at).toLocaleString()}
              {onAccess && u.status === "approved" && (u.assigned_ids?.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`assigned-badges-${u.id}`}>
                  {u.assigned_ids.map((aid) => {
                    const m = everyone.find((x) => x.id === aid);
                    if (!m) return null;
                    return (
                      <span key={aid} data-testid={`assigned-badge-${u.id}-${aid}`}
                        className="text-[9px] tracking-wide uppercase border border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] px-1.5 py-0.5">
                        {m.name} · {m.account_type === "underwriter" ? "UW" : "Partner"}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="md:col-span-2 flex flex-col items-end gap-2">
              <StatusBadge status={u.status} />
              {u.status === "approved" && accessToggle && (
                <button data-testid={`ws-access-toggle-${u.id}`} onClick={() => toggleWorkspaceAccess(u)}
                  className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${u.workspace_role === "staff" ? "border-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "border-white/15 text-white/40 hover:text-white"}`}>
                  Workspace {u.workspace_role === "staff" ? "On" : "Off"}
                </button>
              )}
              {u.status === "approved" && !accessToggle && (
                <div className="flex items-center gap-1">
                  <button data-testid={`ws-role-client-${u.id}`} onClick={() => setWorkspaceRole(u.id, "client")}
                    className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${u.workspace_role !== "staff" ? "border-white/60 text-white" : "border-white/15 text-white/40 hover:text-white"}`}>Client</button>
                  <button data-testid={`ws-role-staff-${u.id}`} onClick={() => setWorkspaceRole(u.id, "staff")}
                    className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${u.workspace_role === "staff" ? "border-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "border-white/15 text-white/40 hover:text-white"}`}>Staff</button>
                </div>
              )}
              {onAccess && u.status === "approved" && (
                <button data-testid={`manage-access-${u.id}`} onClick={() => onAccess(u)}
                  className="text-[10px] tracking-widest uppercase border border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] px-2 py-1 hover:bg-[hsl(var(--gold))]/10 flex items-center gap-1">
                  <UsersIcon className="h-3 w-3" /> Manage access
                </button>
              )}
              {onEngagement && u.status === "approved" && (
                <button data-testid={`engagement-${u.id}`} onClick={() => onEngagement(u)}
                  className="text-[10px] tracking-widest uppercase border border-white/20 text-white/60 px-2 py-1 hover:text-white flex items-center gap-1">
                  <FileText className="h-3 w-3" /> {u.engagement_letter_url ? "Edit engagement link" : "Add engagement link"}
                </button>
              )}
              {u.status === "approved" && ["partner", "underwriter"].includes(u.account_type) && (
                <button data-testid={`set-password-${u.id}`} onClick={() => { setPwUser(u); setPwText(""); }}
                  className="text-[10px] tracking-widest uppercase border border-white/20 text-white/60 px-2 py-1 hover:text-white">
                  Set password
                </button>
              )}
              {u.status === "approved" && u.account_type === "partner" && (
                <button data-testid={`email-toggle-${u.id}`} onClick={() => toggleEmailSend(u)}
                  title={u.email_send_enabled === false ? "Email sending disabled — click to enable" : "Email sending enabled — click to disable"}
                  className={`text-[10px] tracking-widest uppercase border px-2 py-1 flex items-center gap-1 ${u.email_send_enabled === false ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>
                  {u.email_send_enabled === false ? <MailX className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                  Email {u.email_send_enabled === false ? "Off" : "On"}
                </button>
              )}
              {u.status === "approved" && u.workspace_role === "staff" && (
                <button data-testid={`ai-toggle-${u.id}`} onClick={() => toggleAiAccess(u)}
                  title={u.ai_assistant_enabled ? "AI assistant enabled — click to revoke" : "AI assistant disabled — click to grant"}
                  className={`text-[10px] tracking-widest uppercase border px-2 py-1 flex items-center gap-1 ${u.ai_assistant_enabled ? "border-[hsl(var(--gold))]/50 text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10" : "border-white/20 text-white/50 hover:text-white"}`}>
                  <Sparkles className="h-3 w-3" />
                  AI {u.ai_assistant_enabled ? "On" : "Off"}
                </button>
              )}
              {u.status === "approved" && u.workspace_role === "staff" && u.ai_assistant_enabled && (
                <div className="flex items-center gap-1 border border-white/15 px-2 py-1" title="Daily AI messages; blank = role default">
                  <span className="text-[10px] tracking-widest uppercase text-white/40">Limit/day</span>
                  <input data-testid={`ai-limit-${u.id}`} type="number" min="0" max="10000"
                    defaultValue={u.ai_daily_limit ?? ""} placeholder="default"
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    onBlur={(e) => saveAiLimit(u, e.target.value)}
                    className="w-16 bg-transparent text-white/80 text-[11px] outline-none border-b border-white/20 focus:border-[hsl(var(--gold))]" />
                </div>
              )}
              <button data-testid={`edit-profile-${u.id}`} onClick={() => openEdit(u)}
                className="text-[10px] tracking-widest uppercase border border-white/20 text-white/60 px-2 py-1 hover:text-white flex items-center gap-1">
                <Pencil className="h-3 w-3" /> Edit profile
              </button>
              {u.status === "approved" ? (
                <button data-testid={`remove-access-${u.id}`} onClick={() => removeAccess(u.id)}
                  className="text-[10px] tracking-widest uppercase border border-red-500/40 text-red-300 px-2 py-1 hover:bg-red-500/10 flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Remove access
                </button>
              ) : u.status === "rejected" ? (
                <button data-testid={`restore-access-${u.id}`} onClick={() => approve(u.id)}
                  className="text-[10px] tracking-widest uppercase border border-emerald-500/40 text-emerald-300 px-2 py-1 hover:bg-emerald-500/10">
                  Restore access
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    ));
  };

  return (
    <>
      <section className={embedded ? "pb-12" : "pt-32 pb-12 hero-glow"}>
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          {!embedded && <div className="label-kicker">Admin Console</div>}
          {!embedded && (
            <h1 className="font-display text-4xl lg:text-5xl font-light mt-3 leading-tight">
              D.O.X operations.
            </h1>
          )}
          <div className="grid sm:grid-cols-4 gap-px bg-white/10 mt-10 max-w-3xl">
            <Stat n={pending.length} label="Pending users" />
            <Stat n={everyone.length} label="Total accounts" />
            <Stat n={uploads.length} label="Uploads" />
            <Stat n={bookings.length} label="Bookings" />
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Tabs defaultValue="approvals">
            <TabsList className="rounded-none bg-transparent p-0 gap-8 border-b border-white/10">
              <TabsTrigger data-testid="adm-tab-approvals" value="approvals"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Approvals
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-users" value="users"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                All Users
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-clients" value="clients"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Clients
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-partners" value="partners"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Partners
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-readiness" value="readiness"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Readiness
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-uploads" value="uploads"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Uploads
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-bookings" value="bookings"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Bookings
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-connections" value="connections"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Hermes MCP
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-email" value="email"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Email
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-deals" value="deals"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Deals
              </TabsTrigger>
              <TabsTrigger data-testid="adm-tab-vaults" value="vaults"
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                Partner Vaults
              </TabsTrigger>
            </TabsList>

            <TabsContent value="approvals" className="mt-8">
              {pending.length === 0 ? (
                <Empty text="No pending applications right now." />
              ) : (
                <div className="divide-y divide-white/10 border-y border-white/10">
                  {pending.map((u) => (
                    <div key={u.id} className="py-6 grid md:grid-cols-12 gap-4 items-start">
                      <div className="md:col-span-4">
                        <div className="font-display text-xl">{u.name}</div>
                        <div className="text-white/50 text-sm">{u.email}</div>
                        <div data-testid={`acct-type-${u.id}`} className="label-kicker text-[10px] mt-2 text-[hsl(var(--gold))]">
                          {u.account_type === "partner" ? "Partner" : "Client"}
                        </div>
                        <div data-testid={`referral-${u.id}`} className="text-[11px] mt-2 text-white/50">
                          {u.referral_code_used
                            ? <>Referred via code <span className="text-[hsl(var(--gold))] tracking-widest">{u.referral_code_used}</span>
                                {(() => { const p = users.find((x) => x.id === u.partner_id); return p ? ` · ${p.name}` : ""; })()}</>
                            : "No referral, direct application"}
                        </div>
                      </div>
                      <div className="md:col-span-3 text-sm text-white/65">{u.company || ", "}</div>
                      <div className="md:col-span-3 text-sm text-white/65">{u.message || ", "}</div>
                      <div className="md:col-span-2 flex items-center justify-end gap-2">
                        <Button data-testid={`approve-${u.id}`} onClick={() => approve(u.id)}
                          className="rounded-none bg-white text-black hover:bg-white/90">
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`reject-${u.id}`} onClick={() => reject(u.id)}
                          variant="outline" className="rounded-none border-white/20 hover:bg-white/10">
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="users" className="mt-8">
              {everyone.length === 0 ? <Empty text="No users yet." /> : usersList(everyone)}
            </TabsContent>

            <TabsContent value="clients" className="mt-8" data-testid="adm-clients-list">
              {usersList(clients, { onAccess: openAccess, accessToggle: true, onEngagement: openEngagement })}
            </TabsContent>

            <TabsContent value="partners" className="mt-8" data-testid="adm-partners-list">
              <div className="flex justify-end mb-4">
                <button data-testid="add-partner-btn" onClick={() => setAddPartner(true)}
                  className="text-[10px] tracking-widest uppercase border border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] px-3 py-2 hover:bg-[hsl(var(--gold))]/10">
                  + Add partner
                </button>
              </div>
              {usersList(partners, { accessToggle: true })}
            </TabsContent>

            <TabsContent value="readiness" className="mt-8">
              <AdminReadiness />
            </TabsContent>

            <TabsContent value="uploads" className="mt-8">
              {uploads.length === 0 ? <Empty text="No uploads yet." /> : (
                <div className="border-y border-white/10 divide-y divide-white/10">
                  {uploads.map((u) => (
                    <div key={u.id} className="py-4 grid md:grid-cols-12 gap-4 items-center">
                      <div className="md:col-span-5 flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 text-white/40 shrink-0" strokeWidth={1.25} />
                        <div className="min-w-0">
                          <div className="truncate text-sm">{u.original_filename}</div>
                          <div className="text-xs text-white/40">{u.user_name} · {u.user_email}</div>
                        </div>
                      </div>
                      <div className="md:col-span-3 text-xs text-white/40">{new Date(u.created_at).toLocaleString()}</div>
                      <div className="md:col-span-2 text-xs text-white/50">{(u.size/1024).toFixed(1)} KB</div>
                      <div className="md:col-span-2 text-right">
                        <a href={`/api/uploads/${u.id}/download`}
                           target="_blank" rel="noreferrer"
                           data-testid={`adm-download-${u.id}`}
                           className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1">
                          <Download className="h-3 w-3" /> download
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="bookings" className="mt-8">
              {bookings.length === 0 ? <Empty text="No bookings yet." /> : (
                <div className="border-y border-white/10 divide-y divide-white/10">
                  {bookings.map((b) => (
                    <div key={b.id} className="py-5 grid md:grid-cols-12 gap-4 items-center">
                      <div className="md:col-span-4">
                        <div className="font-display text-base">{b.name}</div>
                        <div className="text-white/50 text-xs">{b.email} · {b.phone || ", "}</div>
                      </div>
                      <div className="md:col-span-3 text-sm text-white/65">
                        <CalIcon className="h-3 w-3 inline mr-1 text-white/40" />
                        {b.preferred_date} {b.preferred_time} · {b.timezone}
                      </div>
                      <div className="md:col-span-3 text-sm text-white/65">
                        {b.company || ", "} · {b.capital_range || ", "}
                      </div>
                      <div className="md:col-span-2 text-right">
                        <a href={b.meet_link} target="_blank" rel="noreferrer"
                           className="text-xs text-[hsl(var(--gold))] hover:underline inline-flex items-center gap-1">
                          <Video className="h-3 w-3" /> Jitsi
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="connections" className="mt-8">
              <McpPanel />
            </TabsContent>
            <TabsContent value="email" className="mt-8">
              <EmailSettings everyone={users} />
            </TabsContent>
            <TabsContent value="vaults" className="mt-8">
              <VaultsTab />
            </TabsContent>
            <TabsContent value="deals" className="mt-8" data-testid="adm-deals-list">
              {deals.length === 0 ? <Empty text="No deals in the workspace yet." /> : (
                <div className="border-y border-white/10 divide-y divide-white/10">
                  {deals.map((d) => (
                    <div key={d.id} className="py-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`adm-deal-${d.id}`}>
                      <div className="md:col-span-5">
                        <div className="font-display text-base">{d.title}</div>
                        <div className="text-xs text-white/40 mt-0.5">{d.deal_type}{d.location ? ` · ${d.location}` : ""} · {(d.deal_origin || "company") === "dox" ? "D.O.X Deal" : "Company Deal"}</div>
                      </div>
                      <div className="md:col-span-3 text-sm text-white/60">{d.stage_id?.replace(/_/g, " ")}</div>
                      <div className="md:col-span-2 text-sm text-white/60">{d.deal_lead || "Unassigned"}</div>
                      <div className="md:col-span-2 md:text-right">
                        <button data-testid={`adm-deal-add-task-${d.id}`} onClick={() => openDealTask(d)}
                          className="text-[10px] tracking-widest uppercase border border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] px-2 py-1 hover:bg-[hsl(var(--gold))]/10">
                          Add task
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Dialog open={!!accessClient} onOpenChange={(o) => { if (!o) setAccessClient(null); }}>
        <DialogContent data-testid="client-access-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Client access</DialogTitle>
            <DialogDescription className="text-white/55">
              Choose which partners and underwriters can view {accessClient?.name}'s file and conversations.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-white/10 border-y border-white/10">
            {staffMembers.length === 0 ? (
              <p className="py-6 text-sm text-white/50">No approved partners or underwriters yet.</p>
            ) : staffMembers.map((s) => (
              <label key={s.id} data-testid={`access-option-${s.id}`}
                className="flex items-center gap-3 py-3 cursor-pointer">
                <Checkbox checked={accessIds.includes(s.id)} onCheckedChange={() => toggleAccessId(s.id)}
                  data-testid={`access-check-${s.id}`} className="border-white/30 data-[state=checked]:bg-[hsl(var(--gold))] data-[state=checked]:border-[hsl(var(--gold))]" />
                <span className="flex-1">
                  <span className="block text-sm">{s.name}</span>
                  <span className="block text-xs text-white/45">{s.email}</span>
                </span>
                <span className="label-kicker text-[10px] text-white/40">{s.account_type === "partner" ? "Partner" : "Underwriter"}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-white/35">Underwriters already have firm-wide review access; adding them here also includes them on this deal's conversation and notifications.</p>
          <DialogFooter>
            <Button data-testid="cancel-client-access" variant="outline" onClick={() => setAccessClient(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-client-access" onClick={saveAccess}
              className="rounded-none bg-white text-black hover:bg-white/90">Save access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!engClient} onOpenChange={(o) => { if (!o) setEngClient(null); }}>
        <DialogContent data-testid="engagement-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Engagement letter</DialogTitle>
            <DialogDescription className="text-white/55">
              Paste the Adobe Sign (or other) engagement-letter link for {engClient?.name}. It is emailed to
              the client and shown in their portal under Sign Documents.
            </DialogDescription>
          </DialogHeader>
          <input data-testid="engagement-url-input" value={engUrl} onChange={(e) => setEngUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
          <DialogFooter>
            <Button data-testid="cancel-engagement" variant="outline" onClick={() => setEngClient(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-engagement" onClick={saveEngagement}
              className="rounded-none bg-white text-black hover:bg-white/90">Save &amp; send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dealTask} onOpenChange={(o) => { if (!o) setDealTask(null); }}>
        <DialogContent data-testid="deal-task-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Add task · {dealTask?.title}</DialogTitle>
            <DialogDescription className="text-white/55">Adds a checklist step to this deal in the workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input data-testid="deal-task-title" value={dtForm.title} onChange={(e) => setDtForm((s) => ({ ...s, title: e.target.value }))}
              placeholder="Task title"
              className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <input data-testid="deal-task-date" type="date" value={dtForm.due_date} onChange={(e) => setDtForm((s) => ({ ...s, due_date: e.target.value }))}
              className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <select data-testid="deal-task-assignee" value={dtForm.assignee_id} onChange={(e) => setDtForm((s) => ({ ...s, assignee_id: e.target.value }))}
              className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80">
              {(dealTask?.assigned_ids || []).length === 0 && <option value="">No team assigned to this deal</option>}
              {(dealTask?.assigned_ids || []).map((aid) => {
                const m = directory.find((x) => x.id === aid);
                return <option key={aid} value={aid}>{m?.name || "Team member"}</option>;
              })}
            </select>
          </div>
          <DialogFooter>
            <Button data-testid="cancel-deal-task" variant="outline" onClick={() => setDealTask(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-deal-task" onClick={saveDealTask}
              className="rounded-none bg-white text-black hover:bg-white/90">Add task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) setPwUser(null); }}>
        <DialogContent data-testid="password-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Set password · {pwUser?.name}</DialogTitle>
            <DialogDescription className="text-white/55">Set a new login password for this staff account ({pwUser?.email}).</DialogDescription>
          </DialogHeader>
          <input data-testid="password-input" type="text" value={pwText} onChange={(e) => setPwText(e.target.value)}
            placeholder="New password (min 8 chars)"
            className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
          <DialogFooter>
            <Button data-testid="cancel-password" variant="outline" onClick={() => setPwUser(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-password" onClick={savePw}
              className="rounded-none bg-white text-black hover:bg-white/90">Update password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent data-testid="edit-profile-modal" className="bg-[#0b0b10] border-white/15 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Edit profile · {editUser?.name}</DialogTitle>
            <DialogDescription className="text-white/55">
              Update this {editUser?.account_type === "partner" ? "partner" : editUser?.account_type === "underwriter" ? "underwriter" : "client"}'s details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-kicker text-[10px] text-white/40">Full name</label>
              <input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
                className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            </div>
            <div>
              <label className="label-kicker text-[10px] text-white/40">Sign-in email</label>
              <input data-testid="edit-email" value={editForm.email} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
                className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-kicker text-[10px] text-white/40">Company</label>
                <input data-testid="edit-company" value={editForm.company} onChange={(e) => setEditForm((s) => ({ ...s, company: e.target.value }))}
                  className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
              </div>
              <div>
                <label className="label-kicker text-[10px] text-white/40">Phone</label>
                <input data-testid="edit-phone" value={editForm.phone} onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))}
                  className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
              </div>
            </div>
            <div>
              <label className="label-kicker text-[10px] text-white/40">Referral code</label>
              <input data-testid="edit-referral" value={editForm.referral_code} onChange={(e) => setEditForm((s) => ({ ...s, referral_code: e.target.value.toUpperCase() }))}
                className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            </div>
            <div>
              <label className="label-kicker text-[10px] text-white/40">Business inbox (@thedoxs.com)</label>
              <input data-testid="edit-business-email" value={editForm.business_email} onChange={(e) => setEditForm((s) => ({ ...s, business_email: e.target.value }))}
                placeholder="e.g. david@thedoxs.com" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            </div>
            {editUser && editUser.account_type !== "partner" && editUser.account_type !== "underwriter" && (
              <div>
                <label className="label-kicker text-[10px] text-white/40">Assigned partners / underwriters</label>
                <div className="mt-1 border border-white/10 divide-y divide-white/10 max-h-40 overflow-y-auto">
                  {staffMembers.length === 0 ? (
                    <p className="py-3 px-3 text-xs text-white/40">No approved partners or underwriters yet.</p>
                  ) : staffMembers.map((s) => (
                    <label key={s.id} data-testid={`edit-assign-${s.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5">
                      <Checkbox checked={editForm.assigned_ids.includes(s.id)}
                        onCheckedChange={() => setEditForm((f) => ({ ...f, assigned_ids: f.assigned_ids.includes(s.id) ? f.assigned_ids.filter((x) => x !== s.id) : [...f.assigned_ids, s.id] }))} />
                      <span className="text-sm text-white/80">{s.name}</span>
                      <span className="label-kicker text-[10px] text-white/40 ml-auto">{s.account_type === "partner" ? "Partner" : "Underwriter"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="cancel-edit-profile" variant="outline" onClick={() => setEditUser(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-edit-profile" disabled={editBusy} onClick={saveEdit}
              className="rounded-none bg-white text-black hover:bg-white/90">{editBusy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={addPartner} onOpenChange={setAddPartner}>
        <DialogContent data-testid="add-partner-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Add a partner</DialogTitle>
            <DialogDescription className="text-white/55">Creates an approved partner with workspace access. They log in with the email below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="ap-name" value={apForm.name} onChange={(e) => setApForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="Full name (e.g. David Evans)" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <input data-testid="ap-email" value={apForm.email} onChange={(e) => setApForm((s) => ({ ...s, email: e.target.value }))}
              placeholder="Login email (e.g. david@thedoxs.com)" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <input data-testid="ap-sender" value={apForm.sender_email} onChange={(e) => setApForm((s) => ({ ...s, sender_email: e.target.value }))}
              placeholder="Sender / company email (e.g. david@thedoxs.com)" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <input data-testid="ap-password" type="text" value={apForm.password} onChange={(e) => setApForm((s) => ({ ...s, password: e.target.value }))}
              placeholder="Temporary password (min 8 chars)" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <input data-testid="ap-referral" value={apForm.referral_code} onChange={(e) => setApForm((s) => ({ ...s, referral_code: e.target.value.toUpperCase() }))}
              placeholder="Referral code (optional — auto-generated if blank)" className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
          </div>
          <DialogFooter>
            <Button data-testid="cancel-add-partner" variant="outline" onClick={() => setAddPartner(false)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-add-partner" onClick={saveAddPartner}
              className="rounded-none bg-white text-black hover:bg-white/90">Create partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminPage() {
  return (
    <div className="text-white min-h-screen grain">
      <NavBar />
      <AdminConsole />
      <Footer />
    </div>
  );
}

function VaultsTab() {
  const [owners, setOwners] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fmtSize = (b) => {
    if (!b) return "0 B";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i ? 1 : 0)} ${u[i]}`;
  };

  useEffect(() => {
    api.get("/drive/owners")
      .then((r) => setOwners(r.data.owners || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Empty text="Loading partner vaults..." />;
  if (owners.length === 0) return <Empty text="No partner or underwriter accounts yet." />;

  if (selected) {
    return (
      <div data-testid="adm-vault-detail">
        <button onClick={() => setSelected(null)} data-testid="adm-vault-back"
          className="text-sm text-white/60 hover:text-white mb-6 inline-flex items-center gap-2">
          ← All vaults
        </button>
        <div className="font-display text-2xl mb-1">{selected.name}</div>
        <div className="text-white/50 text-sm mb-6">{selected.email} · {selected.account_type}</div>
        <DriveBrowser ownerId={selected.id}
          emptyText={`${selected.name}'s vault is empty. Upload a file or create a folder for them.`} />
      </div>
    );
  }

  return (
    <div className="border-y border-white/10 divide-y divide-white/10" data-testid="adm-vault-owners">
      {owners.map((o) => (
        <div key={o.id} className="py-5 flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg">{o.name}</div>
            <div className="text-white/50 text-sm">{o.email}</div>
            <div className="label-kicker text-[10px] mt-1 text-[hsl(var(--gold))]">{o.account_type}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right text-xs text-white/40">
              <div>{o.file_count} file{o.file_count === 1 ? "" : "s"}</div>
              <div>{fmtSize(o.total_size)}</div>
            </div>
            <Button data-testid={`adm-vault-open-${o.id}`} variant="outline"
              className="rounded-none border-white/20" onClick={() => setSelected(o)}>
              Open vault
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}


function EmailSettings({ everyone }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ from_name: "", from_email: "", reply_to: "" });
  const [saving, setSaving] = useState(false);
  const [sigUser, setSigUser] = useState(null);
  const [sigText, setSigText] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/email/config").then((r) => {
      setCfg(r.data);
      setForm({ from_name: r.data.from_name || "", from_email: r.data.from_email || "", reply_to: r.data.reply_to || "" });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email/config", form);
      setCfg(data);
      toast.success("Sender identity saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const openSig = (u) => { setSigUser(u); setSigText(u.email_signature || ""); };
  const saveSig = async () => {
    try {
      await api.put(`/admin/users/${sigUser.id}/signature`, { signature: sigText });
      sigUser.email_signature = sigText;
      toast.success(`Signature saved for ${sigUser.name}`);
      setSigUser(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const inboxUsers = (everyone || []).filter((u) => u.role === "admin" || ["partner", "underwriter"].includes(u.account_type));
  const filtered = inboxUsers.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  if (!cfg) return <Empty text="Loading email settings…" />;
  const domain = (cfg.domains || [])[0];

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div data-testid="email-sender-card">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-xl">Sender identity</h3>
          <span data-testid="email-connected" className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${cfg.resend_connected ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
            {cfg.resend_connected ? "Resend connected" : "Not connected"}
          </span>
        </div>
        <p className="text-white/50 text-sm mt-2">Outbound mail across the app is sent from this identity.</p>
        <div className="mt-5 space-y-4 max-w-md">
          <label className="block"><span className="label-kicker text-xs">From name</span>
            <input data-testid="email-from-name" value={form.from_name} onChange={(e) => setForm((s) => ({ ...s, from_name: e.target.value }))}
              className="mt-2 w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" /></label>
          <label className="block"><span className="label-kicker text-xs">From address</span>
            <input data-testid="email-from-address" value={form.from_email} onChange={(e) => setForm((s) => ({ ...s, from_email: e.target.value }))}
              placeholder={`noreply@${cfg.mail_domain}`}
              className="mt-2 w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
            <span className="text-xs text-white/40 mt-1 block">Must be on a verified domain below.</span></label>
          <label className="block"><span className="label-kicker text-xs">Reply-to</span>
            <input data-testid="email-reply-to" value={form.reply_to} onChange={(e) => setForm((s) => ({ ...s, reply_to: e.target.value }))}
              className="mt-2 w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" /></label>
          <Button data-testid="email-save-config" onClick={saveCfg} disabled={saving}
            className="rounded-none bg-white text-black hover:bg-white/90">{saving ? "Saving…" : "Save sender identity"}</Button>
        </div>

        <div className="mt-8" data-testid="email-domains">
          <div className="label-kicker text-xs">Sender domains</div>
          {domain ? (
            <div className="mt-3 border border-white/10 p-4 flex items-center justify-between">
              <div>
                <div className="font-display">{domain.name}</div>
                <div className="text-xs text-white/40 mt-0.5">{domain.region}</div>
              </div>
              <span data-testid="email-domain-status" className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${domain.status === "verified" ? "border-emerald-500/40 text-emerald-300" : "border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))]"}`}>
                {domain.status}
              </span>
            </div>
          ) : (
            <p className="text-white/45 text-sm mt-3">
              {cfg.resend_connected ? "No sender domains found on this Resend account. Add thedoxs.com in Resend, paste the DNS records at your registrar, then refresh." : "Connect a Resend API key to manage domains."}
            </p>
          )}
        </div>
      </div>

      <div data-testid="email-signatures">
        <h3 className="font-display text-xl">Email signatures</h3>
        <p className="text-white/50 text-sm mt-2">Set the signature appended to every email an account sends from the workspace.</p>
        <input data-testid="sig-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…"
          className="mt-4 w-full rounded-none bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/80" />
        <div className="mt-4 border-y border-white/10 divide-y divide-white/10 max-h-[420px] overflow-auto">
          {filtered.length === 0 ? <div className="py-6 text-white/40 text-sm">No inbox-enabled accounts.</div> :
            filtered.map((u) => (
              <div key={u.id} className="py-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm">{u.name}</div>
                  <div className="text-xs text-white/40">{u.business_email || u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.email_signature ? <span className="text-[10px] tracking-widest uppercase text-emerald-300/70">set</span> : <span className="text-[10px] tracking-widest uppercase text-white/30">none</span>}
                  <button data-testid={`sig-edit-${u.id}`} onClick={() => openSig(u)}
                    className="text-[10px] tracking-widest uppercase border border-white/20 text-white/60 px-2 py-1 hover:text-white">Edit signature</button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <Dialog open={!!sigUser} onOpenChange={(o) => { if (!o) setSigUser(null); }}>
        <DialogContent data-testid="signature-modal" className="bg-[#0b0b10] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-light">Signature · {sigUser?.name}</DialogTitle>
            <DialogDescription className="text-white/55">Appended to every email {sigUser?.name} sends from the workspace.</DialogDescription>
          </DialogHeader>
          <RichTextEditor value={sigText} onChange={setSigText}
            placeholder={"Jane Doe\nD.O.X Capital\n+1 555 0100"} />
          <DialogFooter>
            <Button data-testid="cancel-signature" variant="outline" onClick={() => setSigUser(null)}
              className="rounded-none border-white/20 hover:bg-white/10">Cancel</Button>
            <Button data-testid="save-signature" onClick={saveSig}
              className="rounded-none bg-white text-black hover:bg-white/90">Save signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Stat({ n, label }) {
  return (
    <div className="bg-[#050505] p-5">
      <div className="font-display text-3xl tabular">{n}</div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = {
    pending: "border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))]",
    approved: "border-emerald-500/40 text-emerald-300",
    rejected: "border-red-500/40 text-red-300",
  };
  return <span className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${map[status] || ""}`}>{status}</span>;
}
function Empty({ text }) { return <div className="border border-white/10 p-10 text-white/40 text-sm">{text}</div>; }
function CenterMsg({ children }) { return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white/60 text-sm">{children}</div>; }
